import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import Button from '@mui/material/Button';
import Alert from '@mui/material/Alert';
import Autocomplete from '@mui/material/Autocomplete';
import Switch from '@mui/material/Switch';
import FormControlLabel from '@mui/material/FormControlLabel';
import IconButton from '@mui/material/IconButton';
import Skeleton from '@mui/material/Skeleton';
import Layout from '../components/Layout';
import { useI18n } from '../i18n';
import api from '../services/api';
import offices from '../quotes/catalog/offices';
import countries from '../quotes/catalog/countries';
import fmt from '../quotes/catalog/format';

const CURRENCIES = ['MXN', 'USD', 'CLP', 'COP'];
const EMPTY = {
  company: '', contact_name: '', email: '', country: 'MX', currency: 'MXN',
  office: '', payment: '', note: '', discount: 0, tax: 0, products: [],
  is_renewal: false, valid_until: '',
};

function Label({ children }) {
  return (
    <Typography sx={{
      fontSize: 10.5, fontWeight: 500, letterSpacing: '0.09em',
      textTransform: 'uppercase', color: '#847782', mb: 0.4,
    }}>{children}</Typography>
  );
}

export default function QuoteForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useI18n();
  const editing = Boolean(id);

  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(editing);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [catalog, setCatalog] = useState([]);

  // Se propone un mes de vigencia; queda editable.
  useEffect(() => {
    if (editing) return;
    const in30 = new Date(Date.now() + 30 * 86400000);
    setForm((f) => (f.valid_until ? f : { ...f, valid_until: in30.toISOString().slice(0, 10) }));
  }, [editing]);

  const load = useCallback(() => {
    if (!editing) return;
    api.get(`/api/quotes/${id}`)
      .then((res) => setForm({
        ...EMPTY, ...res.data,
        products: res.data.products || [],
        valid_until: res.data.valid_until ? String(res.data.valid_until).slice(0, 10) : '',
      }))
      .catch(() => setError(t('quote.loadError')))
      .finally(() => setLoading(false));
  }, [editing, id, t]);

  useEffect(() => { load(); }, [load]);

  // El catálogo vive en la base: administración lo edita sin desplegar.
  useEffect(() => {
    api.get('/api/products')
      .then((res) => setCatalog(res.data))
      .catch(() => setCatalog([]));
  }, []);

  // Las cotizaciones guardadas antes de copiar la descripción la traen vacía y
  // el PDF sale sin ella. Se completa desde el catálogo al editar. Se busca por
  // nombre: las anteriores guardan el id del catálogo viejo, no el de la base.
  useEffect(() => {
    if (!catalog.length) return;
    setForm((f) => {
      let changed = false;
      const products = f.products.map((p) => {
        if (p.description) return p;
        const cat = catalog.find((c) => c.name === p.name);
        if (!cat?.description) return p;
        changed = true;
        return { ...p, description: cat.description };
      });
      return changed ? { ...f, products } : f;
    });
  }, [catalog, form.products]);

  const set = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));

  // El servidor recalcula al guardar; esto es sólo la vista previa, con la
  // misma fórmula: descuento e impuesto son porcentajes y el impuesto se
  // aplica sobre el subtotal ya descontado.
  const totals = useMemo(() => {
    const clamp = (n) => Math.min(Math.max(Number(n) || 0, 0), 100);
    const subTotal = (form.products || []).reduce(
      (sum, p) => sum + Number(p.quantity || 0) * Number(p.unit_cost || 0), 0
    );
    const discountAmount = subTotal * (clamp(form.discount) / 100);
    const afterDiscount = subTotal - discountAmount;
    const taxAmount = afterDiscount * (clamp(form.tax) / 100);
    return { subTotal, discountAmount, taxAmount, total: afterDiscount + taxAmount };
  }, [form.products, form.discount, form.tax]);

  function addProduct(product) {
    if (!product) return;
    setForm((f) => ({
      ...f,
      products: [...f.products, {
        id: product.id,
        name: product.name,
        description: product.description || '',
        unit: product.unit,
        quantity: 1,
        unit_cost: Number(product.prices?.[f.currency] ?? 0),
      }],
    }));
  }

  function updateProduct(index, field, value) {
    setForm((f) => ({
      ...f,
      products: f.products.map((p, i) => (i === index ? { ...p, [field]: value } : p)),
    }));
  }

  const removeProduct = (index) =>
    setForm((f) => ({ ...f, products: f.products.filter((_, i) => i !== index) }));

  // Al cambiar de moneda hay que reprecificar: el catálogo tiene un precio por
  // moneda y dejar el anterior produciría cotizaciones incorrectas en silencio.
  function changeCurrency(e) {
    const currency = e.target.value;
    setForm((f) => ({
      ...f,
      currency,
      products: f.products.map((p) => {
        const cat = catalog.find((c) => c.id === p.id);
        return cat ? { ...p, unit_cost: Number(cat.prices?.[currency] ?? p.unit_cost) } : p;
      }),
    }));
  }

  async function save() {
    setSaving(true);
    setError(null);
    try {
      const payload = {
        company: form.company, contact_name: form.contact_name, email: form.email,
        country: form.country, currency: form.currency, office: form.office || null,
        payment: form.payment, note: form.note, is_renewal: Boolean(form.is_renewal),
        valid_until: form.valid_until || null,
        discount: Number(form.discount) || 0, tax: Number(form.tax) || 0,
        products: form.products,
      };
      const res = editing
        ? await api.patch(`/api/quotes/${id}`, payload)
        : await api.post('/api/quotes', payload);
      navigate(`/quotes/${res.data.quote_id}`);
    } catch (err) {
      setError(err.response?.data?.error || t('quote.saveError'));
    } finally {
      setSaving(false);
    }
  }

  if (loading) return <Layout><Skeleton variant="rectangular" height={460} /></Layout>;

  return (
    <Layout>
      <Button variant="text" onClick={() => navigate('/quotes')} sx={{ mb: 1, color: '#531253' }}>
        {t('common.back')}
      </Button>

      <Typography variant="h5" sx={{ mb: 2 }}>
        {editing ? `${t('quote.quote')} #${form.q_folio ?? ''}` : t('quote.newQuote')}
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Paper sx={{ p: 2.5, mb: 2 }}>
        <Box sx={{
          display: 'grid', gap: 2.5,
          gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' },
        }}>
          <Box><Label>{t('quote.company')}</Label>
            <TextField fullWidth size="small" value={form.company} onChange={set('company')} /></Box>
          <Box><Label>{t('quote.contact')}</Label>
            <TextField fullWidth size="small" value={form.contact_name} onChange={set('contact_name')} /></Box>
          <Box><Label>Email</Label>
            <TextField fullWidth size="small" type="email" value={form.email} onChange={set('email')}
              helperText={!editing ? t('quote.linkAccount') : ' '} /></Box>

          <Box><Label>{t('quote.country')}</Label>
            <TextField select fullWidth size="small" value={form.country} onChange={set('country')}>
              {countries.map((c) => (
                <MenuItem key={c.code} value={c.code}>{c.label}</MenuItem>
              ))}
            </TextField></Box>
          <Box><Label>{t('quote.currency')}</Label>
            <TextField select fullWidth size="small" value={form.currency} onChange={changeCurrency}>
              {CURRENCIES.map((c) => <MenuItem key={c} value={c}>{c}</MenuItem>)}
            </TextField></Box>
          <Box><Label>{t('quote.office')}</Label>
            <TextField select fullWidth size="small" value={form.office || ''} onChange={set('office')}>
              <MenuItem value="">—</MenuItem>
              {offices.map((o) => (
                <MenuItem key={o.office_id} value={o.office_id}>{o.name}</MenuItem>
              ))}
            </TextField></Box>

          <Box><Label>{t('quote.payment')}</Label>
            <TextField fullWidth size="small" value={form.payment} onChange={set('payment')} /></Box>

          <Box><Label>{t('quote.validUntil')}</Label>
            <TextField fullWidth size="small" type="date"
              value={form.valid_until || ''} onChange={set('valid_until')}
              helperText={t('quote.validUntilHelp')} /></Box>
          <Box><Label>{t('quote.note')}</Label>
            <TextField fullWidth size="small" value={form.note || ''} onChange={set('note')} /></Box>

          <Box>
            <Label>{t('quote.type')}</Label>
            <FormControlLabel
              sx={{ ml: 0 }}
              control={
                <Switch
                  checked={Boolean(form.is_renewal)}
                  onChange={(e) => setForm((f) => ({ ...f, is_renewal: e.target.checked }))}
                />
              }
              label={
                <Box>
                  <Typography sx={{ fontSize: 13.5 }}>
                    {form.is_renewal ? t('quote.isRenewal') : t('quote.newClient')}
                  </Typography>
                  <Typography sx={{ fontSize: 11.5, color: '#847782' }}>
                    {t('quote.renewalHelp')}
                  </Typography>
                </Box>
              }
            />
          </Box>
        </Box>
      </Paper>

      <Paper sx={{ p: 2.5, mb: 2 }}>
        <Label>{t('quote.products')}</Label>
        <Autocomplete
          options={catalog}
          getOptionLabel={(o) => o.name}
          onChange={(_, value) => addProduct(value)}
          value={null}
          blurOnSelect
          renderInput={(params) => (
            <TextField {...params} size="small" placeholder={t('quote.products')} sx={{ mb: 2 }} />
          )}
        />

        {form.products.length === 0 && (
          <Typography sx={{ fontSize: 13.5, color: '#847782' }}>—</Typography>
        )}

        {form.products.map((p, i) => (
          <Box key={`${p.id}-${i}`} sx={{
            display: 'grid', gap: 1.5, alignItems: 'center', py: 1.2,
            gridTemplateColumns: { xs: '1fr', sm: '1fr 90px 130px 120px 40px' },
            borderBottom: '1px solid #F0ECEE',
          }}>
            <Box sx={{ fontSize: 13.5 }}>{p.name}</Box>
            <TextField size="small" type="number" value={p.quantity}
              onChange={(e) => updateProduct(i, 'quantity', Number(e.target.value))}
              inputProps={{ min: 0 }} />
            <TextField size="small" type="number" value={p.unit_cost}
              onChange={(e) => updateProduct(i, 'unit_cost', Number(e.target.value))} />
            <Box sx={{ fontSize: 13.5, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
              {fmt.getCurrencyFormat((p.quantity || 0) * (p.unit_cost || 0), form.currency)}
            </Box>
            <IconButton size="small" onClick={() => removeProduct(i)} sx={{ color: '#AE5B61' }}>×</IconButton>
          </Box>
        ))}

        <Box sx={{
          display: 'grid', gap: 2, mt: 2.5, pt: 2, borderTop: '1px solid #E4E0E2',
          gridTemplateColumns: { xs: '1fr', sm: 'repeat(4, 1fr)' }, alignItems: 'end',
        }}>
          <Box><Label>{t('general.subtotal')}</Label>
            <Box sx={{ fontSize: 15, fontVariantNumeric: 'tabular-nums' }}>
              {fmt.getCurrencyFormat(totals.subTotal, form.currency)}
            </Box></Box>

          <Box><Label>{t('general.discount')} %</Label>
            <TextField fullWidth size="small" type="number" value={form.discount}
              onChange={set('discount')} inputProps={{ min: 0, max: 100, step: 0.5 }}
              helperText={fmt.getCurrencyFormat(-totals.discountAmount, form.currency)} /></Box>

          <Box><Label>{t('general.taxes')} %</Label>
            <TextField fullWidth size="small" type="number" value={form.tax}
              onChange={set('tax')} inputProps={{ min: 0, max: 100, step: 0.5 }}
              helperText={fmt.getCurrencyFormat(totals.taxAmount, form.currency)} /></Box>

          <Box><Label>{t('quote.total')}</Label>
            <Box sx={{ fontSize: 19, fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
              {fmt.getCurrencyFormat(totals.total, form.currency)}
            </Box></Box>
        </Box>
      </Paper>

      <Box sx={{ display: 'flex', gap: 1.5 }}>
        <Button variant="contained" disabled={saving || !form.company} onClick={save}>
          {t('common.save')}
        </Button>
        <Button variant="outlined" onClick={() => navigate('/quotes')}
          sx={{ color: '#531253', borderColor: '#E4E0E2' }}>
          {t('common.close')}
        </Button>
      </Box>
    </Layout>
  );
}
